import React from 'react';
import clsx from 'clsx';
import * as Headless from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/24/outline';

export const FilterButton = <T,>(props: {
  label: string;
  multiple?: boolean;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement> & { className?: string }>;
  values: { label: string; value: T }[];
  initialValues?: T[];
  onChange: (values: T[]) => void;
}) => {
  const [selected, setSelected] = React.useState(props.initialValues || []);
  const anySelected = selected.length > 0;
  const selectedCount = selected.length;

  const onChangeRef = React.useRef(props.onChange);
  onChangeRef.current = props.onChange;

  // Sync internal state when the parent value changes externally
  // (e.g. sort/group changed from DataTable header click)
  const serializedInitial = JSON.stringify(props.initialValues || []);
  React.useEffect(() => {
    setSelected(JSON.parse(serializedInitial));
  }, [serializedInitial]);

  const handleSelect = (value: T) => {
    const next = props.multiple
      ? selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value]
      : selected.includes(value)
        ? []
        : [value];
    setSelected(next);
    onChangeRef.current(next);
  };

  return (
    <Headless.Menu
      as="div"
      className={clsx(
        'relative',
        'focus:outline-none after:pointer-events-none after:absolute after:inset-0 after:rounded-full after:ring-inset after:ring-transparent after:focus-within:ring-0 after:focus-within:ring-neutral-300 dark:after:focus-within:ring-neutral-600'
      )}
    >
      <Headless.MenuButton
        className={clsx(
          'inline-flex w-full items-center justify-center gap-x-1 rounded-full px-3 py-1.5',
          'focus:outline-none after:pointer-events-none after:absolute after:inset-0 after:rounded-full after:ring-inset after:ring-transparent after:focus-within:ring-0 after:focus-within:ring-neutral-300 dark:after:focus-within:ring-neutral-600',
          !anySelected &&
            'text-neutral-700 dark:text-neutral-300 bg-white/50 dark:bg-neutral-800/50 border border-neutral-300 dark:border-neutral-600 border-dashed hover:bg-neutral-50/50 dark:hover:bg-neutral-700/50',
          anySelected &&
            'text-blue-600/80 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 border border-blue-600/80 dark:border-blue-500'
        )}
      >
        <div>
          {!anySelected && (
            <props.icon
              aria-hidden="true"
              className="h-3.5 w-3.5 text-neutral-500 dark:text-neutral-400"
            />
          )}
          {anySelected && (
            <props.icon
              aria-hidden="true"
              className="h-3.5 w-3.5 text-blue-600/80 dark:text-blue-400"
            />
          )}
        </div>
        <div className="align-middle text-xs font-medium">{props.label}</div>
        {selectedCount > 0 && (
          <div className="text-xs font-medium rounded-md ml-1 px-1.5 bg-blue-600/80 dark:bg-blue-500 text-white">
            {selectedCount}
          </div>
        )}
      </Headless.MenuButton>
      <Headless.MenuItems
        transition
        className="absolute left-0 z-10 mt-2 px-2 py-2 flex flex-col gap-y-0.5 origin-top-right rounded-lg bg-white dark:bg-neutral-800 shadow-lg ring-1 ring-black/5 dark:ring-white/10 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in w-auto min-w-max"
      >
        {props.values.map(({ label, value }) => (
          <Headless.MenuItem
            as="div"
            key={value as string}
            className={clsx(
              'flex flex-grow rounded-md cursor-pointer px-3.5 py-1.5 text-sm data-[focus]:outline-none',
              !selected.includes(value) &&
                'text-neutral-700 dark:text-neutral-300 data-[focus]:bg-neutral-100 dark:data-[focus]:bg-neutral-700 data-[focus]:text-neutral-900 dark:data-[focus]:text-white',
              selected.includes(value) &&
                'font-medium text-blue-600/80 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 data-[focus]:bg-blue-50 dark:data-[focus]:bg-blue-900/30 data-[focus]:text-blue-600/80 dark:data-[focus]:text-blue-400'
            )}
            onClick={(e) => {
              e.preventDefault();
              handleSelect(value);
            }}
          >
            <div className="flex flex-row items-center justify-between gap-x-8 w-full">
              <div className="">{label}</div>
              <div className={clsx(selected.includes(value) ? 'visible' : 'invisible')}>
                <CheckIcon className="size-4" />
              </div>
            </div>
          </Headless.MenuItem>
        ))}
      </Headless.MenuItems>
    </Headless.Menu>
  );
};
